import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Slim, single-line CTA used mid-section to nudge conversion
 * without breaking scroll rhythm. Complements <CtaBanner /> (which is bigger).
 *
 * Variants:
 *  - "gold"   → bright yellow bar with navy text (highest attention)
 *  - "navy"   → dark navy bar with white text + gold accent line
 *  - "ghost"  → white/muted bar with subtle gold border (least intrusive)
 *
 * Props:
 *  - eyebrow?: small uppercase label ("Próximo passo", "Dica")
 *  - title: main line (bold)
 *  - to: destination (string) — internal by default
 *  - label: CTA button text
 *  - external?: true → opens in new tab (WhatsApp etc)
 *  - icon?: Lucide icon component to prefix the CTA
 *  - variant?: "gold" | "navy" | "ghost"
 *  - testId?: data-testid
 */
export default function InlineCTA({
    eyebrow,
    title,
    to = "/contacto",
    label = "Peça o seu",
    external = false,
    icon: Icon,
    variant = "gold",
    testId = "inline-cta",
    className = "",
}) {
    const isGold = variant === "gold";
    const isNavy = variant === "navy";
    const isGhost = variant === "ghost";

    const wrapper = cn(
        "relative overflow-hidden rounded-2xl border transition block",
        isGold && "bg-secondary border-secondary/60 text-primary shadow-[0_10px_30px_-16px_rgba(224,174,50,0.55)]",
        isNavy && "gradient-navy border-white/10 text-white shadow-lg",
        isGhost && "bg-white border-secondary/30 text-primary hover:border-secondary/60 hover:shadow-md",
        className
    );

    const eyebrowCls = cn(
        "text-[10px] font-bold uppercase tracking-[0.24em]",
        isGold && "text-primary/70",
        isNavy && "text-secondary",
        isGhost && "text-secondary"
    );

    const titleCls = cn(
        "font-heading text-lg sm:text-xl font-extrabold leading-snug",
        isGold && "text-primary",
        isNavy && "text-white",
        isGhost && "text-primary"
    );

    const btnCls = cn(
        "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition hover:-translate-y-0.5 shadow-sm",
        isGold && "bg-primary text-primary-foreground hover:bg-primary/90",
        isNavy && "bg-secondary text-primary hover:bg-yellow-300",
        isGhost && "bg-primary text-primary-foreground hover:bg-primary/90"
    );

    const inner = (
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 px-5 py-4 sm:px-7 sm:py-5">
            {/* Left accent line for navy variant */}
            {isNavy && <span aria-hidden className="hidden sm:block h-10 w-[3px] rounded-full bg-secondary flex-shrink-0" />}
            <div className="min-w-0 flex-1">
                {eyebrow && <span className={eyebrowCls}>{eyebrow}</span>}
                <p className={cn(titleCls, eyebrow && "mt-1")}>{title}</p>
            </div>
            <span className={btnCls}>
                {Icon ? <Icon className="h-4 w-4" /> : null}
                {label}
                <ArrowRight className="h-4 w-4 arrow-shift" />
            </span>
        </div>
    );

    if (external) {
        return (
            <a
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                className={wrapper}
                data-testid={testId}
            >
                {isGold && (
                    <div aria-hidden className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-primary/10 pointer-events-none" />
                )}
                {inner}
            </a>
        );
    }
    return (
        <Link to={to} className={wrapper} data-testid={testId}>
            {isGold && (
                <div aria-hidden className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-primary/10 pointer-events-none" />
            )}
            {inner}
        </Link>
    );
}
